import { useEffect, useState, useMemo } from "react";
import api from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import SearchBar from "@/components/SearchBar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Trash, PencilSimple, Prohibit, Shield } from "@phosphor-icons/react";

const emptyForm = { name: "", phone: "", address: "", id_number: "", blacklist: false, blacklist_reason: "" };

export default function Customers() {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [showBlacklistOnly, setShowBlacklistOnly] = useState(false);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);

  const load = () => api.get("/customers").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    let list = items;
    if (showBlacklistOnly) list = list.filter((it) => it.blacklist);
    if (!s) return list;
    return list.filter((it) =>
      (it.name || "").toLowerCase().includes(s) ||
      (it.phone || "").toLowerCase().includes(s) ||
      (it.id_number || "").toLowerCase().includes(s) ||
      (it.address || "").toLowerCase().includes(s)
    );
  }, [items, q, showBlacklistOnly]);

  const blacklistCount = items.filter((c) => c.blacklist).length;

  const openNew = () => { setEditing(null); setForm(emptyForm); setOpen(true); };
  const openEdit = (it) => { setEditing(it); setForm({ ...emptyForm, ...it }); setOpen(true); };

  const submit = async () => {
    try {
      if (editing) await api.put(`/customers/${editing.id}`, form);
      else await api.post("/customers", form);
      toast.success("Tersimpan"); setOpen(false); load();
    } catch (e) { toast.error("Gagal menyimpan"); }
  };

  const del = async (id) => {
    if (!window.confirm("Hapus pelanggan?")) return;
    await api.delete(`/customers/${id}`); toast.success("Terhapus"); load();
  };

  return (
    <div>
      <PageHeader title="Pelanggan" subtitle={`Data pembeli / debitur${blacklistCount ? ` · ${blacklistCount} di blacklist` : ""}`}
        actions={
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button onClick={openNew} data-testid="new-customer-btn" className="rounded-none bg-[#0A0A0A] hover:bg-gray-800 text-white uppercase tracking-wider text-xs h-10">
                <Plus size={14} className="mr-2" /> Tambah
              </Button>
            </DialogTrigger>
            <DialogContent className="rounded-none max-w-[95vw] max-h-[92vh] overflow-y-auto">
              <DialogHeader><DialogTitle className="font-display text-xl">{editing ? "Edit" : "Tambah"} Pelanggan</DialogTitle></DialogHeader>
              <div className="space-y-3 pt-2">
                <div><Label className="text-xs uppercase tracking-wider">Nama</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-2 rounded-none" data-testid="customer-name" /></div>
                <div><Label className="text-xs uppercase tracking-wider">Telepon</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="mt-2 rounded-none font-mono-num" data-testid="customer-phone" /></div>
                <div><Label className="text-xs uppercase tracking-wider">No. KTP</Label><Input value={form.id_number} onChange={(e) => setForm({ ...form, id_number: e.target.value })} className="mt-2 rounded-none font-mono-num" data-testid="customer-ktp" /></div>
                <div><Label className="text-xs uppercase tracking-wider">Alamat</Label><Textarea value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} className="mt-2 rounded-none" data-testid="customer-address" /></div>
                <div className={`border p-3 ${form.blacklist ? "border-[#E63946] bg-red-50" : "border-gray-200"}`}>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" checked={!!form.blacklist} onChange={(e) => setForm({ ...form, blacklist: e.target.checked })} data-testid="customer-blacklist" className="w-4 h-4 accent-[#E63946]" />
                    <Prohibit size={16} className={form.blacklist ? "text-[#E63946]" : "text-gray-400"} weight="bold" />
                    <span className="text-sm font-semibold">Masukkan ke Blacklist</span>
                  </label>
                  {form.blacklist && (
                    <div className="mt-3">
                      <Label className="text-xs uppercase tracking-wider">Alasan Blacklist</Label>
                      <Input value={form.blacklist_reason} onChange={(e) => setForm({ ...form, blacklist_reason: e.target.value })} placeholder="Contoh: menunggak lama, penipuan..." className="mt-2 rounded-none" data-testid="customer-blacklist-reason" />
                    </div>
                  )}
                </div>
                <Button onClick={submit} data-testid="customer-submit" className="w-full rounded-none bg-[#0A0A0A] hover:bg-gray-800 text-white uppercase tracking-wider text-xs h-11">Simpan</Button>
              </div>
            </DialogContent>
          </Dialog>
        }
      />
      <div className="p-4 md:p-6 lg:p-10">
        <div className="flex items-center gap-3 mb-4">
          <div className="flex-1"><SearchBar value={q} onChange={setQ} placeholder="Cari nama / telepon / KTP..." testid="customers-search" count={filtered.length} /></div>
          <button
            onClick={() => setShowBlacklistOnly(!showBlacklistOnly)}
            data-testid="toggle-blacklist"
            className={`h-10 px-4 text-xs uppercase tracking-wider border transition-colors ${showBlacklistOnly ? "bg-[#E63946] border-[#E63946] text-white" : "border-gray-900 hover:bg-gray-900 hover:text-white"}`}
          >
            {showBlacklistOnly ? "Semua" : `Blacklist (${blacklistCount})`}
          </button>
        </div>
        <div className="border border-gray-200 overflow-x-auto overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr className="text-left text-[10px] uppercase tracking-widest text-gray-600">
                <th className="p-4">Nama</th><th className="p-4">Telepon</th><th className="p-4">No. KTP</th><th className="p-4">Alamat</th><th className="p-4">Status</th><th className="p-4"></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((it) => (
                <tr key={it.id} className={`border-b border-gray-100 ${it.blacklist ? "bg-red-50/40" : ""}`} data-testid={`customer-row-${it.id}`}>
                  <td className="p-4 font-semibold">{it.name}</td>
                  <td className="p-4 font-mono-num text-xs">{it.phone || "-"}</td>
                  <td className="p-4 font-mono-num text-xs">{it.id_number || "-"}</td>
                  <td className="p-4 text-gray-600 text-xs">{it.address || "-"}</td>
                  <td className="p-4">
                    {it.blacklist ? (
                      <div className="inline-flex items-center gap-1 px-2 py-1 bg-[#E63946] text-white text-[10px] uppercase tracking-wider" title={it.blacklist_reason || ""}>
                        <Prohibit size={10} weight="bold" /> Blacklist
                      </div>
                    ) : (
                      <div className="inline-flex items-center gap-1 px-2 py-1 border border-gray-900 text-[10px] uppercase tracking-wider">
                        <Shield size={10} weight="bold" /> Aktif
                      </div>
                    )}
                  </td>
                  <td className="p-4 text-right">
                    <button onClick={() => openEdit(it)} className="mr-3 text-gray-500 hover:text-black"><PencilSimple size={16} /></button>
                    <button onClick={() => del(it.id)} className="text-gray-500 hover:text-[#E63946]"><Trash size={16} /></button>
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && <tr><td colSpan="6" className="p-10 text-center text-sm text-gray-500">{items.length === 0 ? "Belum ada pelanggan" : "Tidak ada hasil"}</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
